import { definePreset, updatePreset } from "@primeuix/themes"
import { defineStore } from "pinia"
import { ref, computed } from "vue"
import {
	ThemeMgmClient,
	ThemeTemplate,
	ThemeState,
	AppSettingClient,
	// FontSettingsClient, // No longer used - font settings are stored in JSON
} from "../../entities/NSwagGenerated"
import Aura from "@primeuix/themes/aura"
import axios from "axios"

// Local interface to replace the removed AppSettings_fontSettings
interface FontSettings {
	id: string
	themeId?: string
	fontFamily?: string
	fontSize?: number
	fontWeight?: string
	letterSpacing?: number
	lineHeight?: number
	textDecoration?: string
	textTransform?: string
	color?: string
	isDefault?: boolean
	toJSON: (data?: any) => any
	init: (_data?: any) => void
	[key: string]: any
}

const themeManagerClient = new ThemeMgmClient(import.meta.env.VITE_API_URL?.toString())
// const appSettingClient = new AppSettingClient(import.meta.env.VITE_API_URL?.toString()) // Unused
// const fontSettingsClient = new FontSettingsClient(import.meta.env.VITE_API_URL?.toString()) // No longer used - font settings are stored in JSON

// Module-level helper — must be outside the store so convertTemplateUIToPreset can use it
function lightenColor(hex: string, percent: number): string {
	hex = hex.replace(/^#/, "")
	let r = parseInt(hex.substring(0, 2), 16)
	let g = parseInt(hex.substring(2, 4), 16)
	let b = parseInt(hex.substring(4, 6), 16)
	r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)))
	g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)))
	b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)))
	return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`
}

// Helper function to convert TemplateUI to PrimeVue Preset format
function convertTemplateUIToPreset(templateUI: any): any {
	try {
		// Parse the themeSettings JSON string if it exists
		const themeSettings = templateUI.themeSettings ? JSON.parse(templateUI.themeSettings) : {}

		// Create a PrimeVue-compatible preset object
		const preset = {
			semantic: {
				primary:
					themeSettings.semantic?.primary && typeof themeSettings.semantic?.primary === "object" && themeSettings.semantic?.primary.color
						? themeSettings.semantic?.primary
						: themeSettings.semantic?.primary || {
								"50": "#fef7ff",
								"100": "#fdeeff",
								"200": "#fbdcff",
								"300": "#f8c0ff",
								"400": "#f396ff",
								"500": "#ec66ff",
								"600": "#d946ef",
								"700": "#c026d3",
								"800": "#a21caf",
								"900": "#86198f",
								"950": "#581c87",
							},
				colorScheme: {
					light: themeSettings.semantic?.colorScheme?.light || {
						primary: {
							color: themeSettings.semantic?.primary?.["600"] || "#d946ef",
							inverseColor: "#ffffff",
							hoverColor: themeSettings.semantic?.primary?.["700"] || "#c026d3",
							activeColor: themeSettings.semantic?.primary?.["800"] || "#a21caf",
						},
						highlight: {
							background: themeSettings.semantic?.primary?.["50"] || "#fef7ff",
							focusBackground: themeSettings.semantic?.primary?.["100"] || "#fdeeff",
							color: themeSettings.semantic?.primary?.["700"] || "#c026d3",
							focusColor: themeSettings.semantic?.primary?.["800"] || "#a21caf",
						},
					},
					dark: themeSettings.semantic?.colorScheme?.dark || {
						primary: {
							color: themeSettings.semantic?.primary?.["400"] || "#f396ff",
							inverseColor: themeSettings.semantic?.primary?.["950"] || "#581c87",
							hoverColor: themeSettings.semantic?.primary?.["300"] || "#f8c0ff",
							activeColor: themeSettings.semantic?.primary?.["200"] || "#fbdcff",
						},
						highlight: {
							background: "color-mix(in srgb, " + (themeSettings.semantic?.primary?.["400"] || "#f396ff") + ", transparent 84%)",
							focusBackground: "color-mix(in srgb, " + (themeSettings.semantic?.primary?.["400"] || "#f396ff") + ", transparent 76%)",
							color: "rgba(255,255,255,.87)",
							focusColor: "rgba(255,255,255,.87)",
						},
					},
				},
				// Include custom ERM brand colors if they exist
				...themeSettings.semantic,
			},
			components: themeSettings.components || {},
		}

		// Ensure datatable/treetable sort icons follow the header cell text color.
		// Aura's default uses {text.muted.color} (gray), which ignores the configured header text color.
		if (preset.components?.datatable && !preset.components.datatable.sortIcon) {
			preset.components.datatable.sortIcon = {
				color: "{datatable.header.cell.color}",
				hoverColor: "{datatable.header.cell.color}",
				size: "0.875rem",
			}
		}
		if (preset.components?.treetable && !preset.components.treetable.sortIcon) {
			preset.components.treetable.sortIcon = {
				color: "{treetable.header.cell.color}",
				hoverColor: "{treetable.header.cell.color}",
				size: "0.875rem",
			}
		}

		// Ensure headerCell hoverColor does not change on hover — keep the configured text color.
		// Aura's default uses {content.hover.color} which may differ from the configured header color.
		for (const compKey of ["datatable", "treetable"] as const) {
			const comp = preset.components?.[compKey]
			if (!comp) continue
			const tokenBase = compKey === "datatable" ? "datatable.header.cell.color" : "treetable.header.cell.color"
			for (const scheme of ["light", "dark"] as const) {
				const headerCell = (comp as any).colorScheme?.[scheme]?.headerCell
				if (headerCell && !headerCell.hoverColor) {
					headerCell.hoverColor = `{${tokenBase}}`
				}
			}
		}

		// Ensure button severities always have hoverBackground and hoverColor set.
		// Without hoverColor, PrimeVue/Aura computes a dark label from the primary palette on hover.
		// Without hoverBackground, the browser falls back to the default which may look wrong.
		const buttonSchemes = preset.components?.button?.colorScheme
		if (buttonSchemes) {
			for (const scheme of Object.values(buttonSchemes) as any[]) {
				for (const severity of Object.values(scheme?.root ?? {}) as any[]) {
					if (severity?.color && !severity.hoverColor) {
						severity.hoverColor = severity.color
					}
					if (severity?.background && !severity.hoverBackground) {
						const bg = severity.background as string
						severity.hoverBackground = bg.startsWith("#")
							? lightenColor(bg, 15)
							: `color-mix(in srgb, ${bg} 85%, white)`
					}
				}
			}
		}

		return preset
	} catch (error) {
		console.error("Error parsing theme settings:", error)
		// Return a default preset if parsing fails
		return {
			semantic: {},
			components: {},
		}
	}
}

const ermbrandTheme = {
	gray: {
		0: "rgb(36, 39, 41)",
		1: "rgb(68, 73, 76)",
		2: "rgb(75, 81, 85)",
		3: "rgb(83, 90, 94)",
		4: "rgb(93, 100, 105)",
		5: "rgb(103, 112, 116)",
		6: "rgb(114, 124, 129)",
		7: "rgb(161, 168, 172)",
		8: "rgb(192, 197, 199)",
		9: "rgb(223, 225, 226)",
	},
	navyblue: {
		0: "rgb(0, 21, 41)",
		1: "rgb(0, 33, 64)",
		2: "rgb(0, 36, 71)",
		3: "rgb(0, 41, 78)",
		4: "rgb(0, 45, 87)",
		5: "rgb(0, 50, 97)",
		6: "rgb(19, 76, 130)",
		7: "rgb(50, 137, 219)",
		8: "rgb(126, 176, 223)",
		9: "rgb(175, 202, 228)",
	},
	green: {
		0: "rgb(0, 64,44)",
		1: "rgb(0, 84,64)",
		2: "rgb(0, 97,75)",
		3: "rgb(0, 107,83)",
		4: "rgb(0, 117,91)",
		5: "rgb(0, 122,95)",
		6: "rgb(75, 137,117)",
		7: "rgb(132, 166,153)",
		8: "rgb(166, 188,179)",
		9: "rgb(194, 208,202)",
	},
	olive: {
		0: "rgb(103, 96,0)",
		1: "rgb(123, 116,0)",
		2: "rgb(141, 134,0)",
		3: "rgb(156, 148,0)",
		4: "rgb(170, 162,0)",
		5: "rgb(177, 168,0)",
		6: "rgb(184, 176,75)",
		7: "rgb(199, 193,132)",
		8: "rgb(211, 207,166)",
		9: "rgb(224, 221,194)",
	},
	orange: {
		0: "rgb(127, 73,0)",
		1: "rgb(147, 93,0)",
		2: "rgb(168, 107,0)",
		3: "rgb(186, 119,0)",
		4: "rgb(202, 130,0)",
		5: "rgb(210, 135,0)",
		6: "rgb(214, 148,75)",
		7: "rgb(221, 173,132)",
		8: "rgb(228, 193,166)",
		9: "rgb(194, 208,202)",
	},
	red: {
		0: "rgb(117, 0,24)",
		1: "rgb(137, 0,44)",
		2: "rgb(157, 0,52)",
		3: "rgb(173, 0,58)",
		4: "rgb(189, 0,64)",
		5: "rgb(196, 0,67)",
		6: "rgb(201, 75,98)",
		7: "rgb(212, 132,143)",
		8: "rgb(221, 166,172)",
		9: "rgb(230, 194,198)",
	},
	blue: {
		0: "rgb(0, 77,129)",
		1: "rgb(0, 97,149)",
		2: "rgb(0, 112,172)",
		3: "rgb(0, 124,189)",
		4: "rgb(0, 136,206)",
		5: "rgb(0, 141,214)",
		6: "rgb(75, 153,217)",
		7: "rgb(132, 176,224)",
		8: "rgb(166, 195,231)",
		9: "rgb(194, 213,237)",
	},
	grayblue: {
		0: "rgb(52, 70,104)",
		1: "rgb(72, 90,124)",
		2: "rgb(84, 104,142)",
		3: "rgb(93, 115,157)",
		4: "rgb(102, 126,171)",
		5: "rgb(106, 131,178)",
		6: "rgb(125, 145,185)",
		7: "rgb(158, 171,199)",
		8: "rgb(182, 191,212)",
		9: "rgb(204, 210,224)",
	},
}

export const useThemeManagerStore = defineStore("themeStatusStore", () => {
	// State
	const themeState = ref<ThemeState>({} as ThemeState)
	const fontSettings = ref<FontSettings[]>([])
	const id = computed({
		get: () => themeState.value.id,
		set: (val) => (themeState.value.id = val),
	})
	const templateUI = computed({
		get: () => themeState.value.templateUI,
		set: (val) => (themeState.value.templateUI = val),
	})
	const selectedTheme = computed({
		get: () => themeState.value.selectedTheme,
		set: (val) => (themeState.value.selectedTheme = val),
	})

	// Actions
	async function loadSingleTemplate(selectedTemplateId: string): Promise<ThemeTemplate | undefined> {
		try {
			const res = await themeManagerClient.getOneThemeSettings(selectedTemplateId)
			if (res) {
				res.semantic!.ermbrand = ermbrandTheme

				templateUI.value = res

				// Also update PrimeVue preset with the loaded template
				const preset = convertTemplateUIToPreset({ themeSettings: JSON.stringify(res) })
				updatePreset(definePreset(Aura, preset))

				return res
			} else {
				return undefined
			}
		} catch (err) {
			throw new Error(`Error from API: ${err}`)
		}
	}

	function applyButtonHoverDefaults(themeState: ThemeState): void {
		// ThemeState has selectedTheme.components — not components directly
		const buttonColorScheme = (themeState as any)?.selectedTheme?.components?.button?.colorScheme
		if (!buttonColorScheme) return

		for (const scheme of Object.values(buttonColorScheme) as any[]) {
			for (const severity of Object.values(scheme?.root ?? {}) as any[]) {
				if (severity?.background) {
					const bg = severity.background as string
					// lightenColor only works on hex — use color-mix for CSS var references
					severity.hoverBackground = bg.startsWith("#")
						? lightenColor(bg, 15)
						: `color-mix(in srgb, ${bg} 85%, white)`
				}
				if (severity?.color) {
					severity.hoverColor = severity.color
				}
			}
		}
	}

	async function saveSingleTemplate(selectedThemeParam: ThemeState, context: string = "live"): Promise<ThemeState | undefined> {
		try {
			applyButtonHoverDefaults(selectedThemeParam)
			const res = await themeManagerClient.saveCompleteThemeSettings(selectedThemeParam)
			if (res) {
				// Only update the live store values if we're NOT in configuration mode
				// This prevents the logo and other theme settings from being applied to the main app
				// when saving from the theme configuration editor
				if (context !== "configuration") {
					templateUI.value = res.templateUI
					selectedTheme.value = res.selectedTheme

					selectedTheme.value!.semantic!.ermbrand = ermbrandTheme
				}

				return res
			} else {
				return undefined
			}
		} catch (err) {
			throw new Error(`Error from API: ${err}`)
		}
	}

	async function loadSingleCompleteTemplate(selectedTemplateId: string, context: string): Promise<ThemeState | undefined> {
		try {
			const res = await themeManagerClient.getCompleteThemeSetting(selectedTemplateId)
			if (res) {
				// In configuration mode, don't update the live store values
				// Just return the theme data for preview purposes
				if (context === "configuration") {
					res.selectedTheme!.semantic!.ermbrand = ermbrandTheme
					// Load font settings from the returned theme's JSON directly
					const loadedFontSettings = loadFontSettingsFromJSON(res.templateUI)

					// Attach the font settings to the selectedTheme for use in configuration
					if (loadedFontSettings && loadedFontSettings.length > 0) {
						res.selectedTheme!.fontSettings = loadedFontSettings[0]
					} else {
						console.log("⚠️ No font settings loaded from JSON")
					}
					return res
				}

				// For live application, update the store and apply theme
				id.value = res.id
				templateUI.value = res.templateUI
				selectedTheme.value = res.selectedTheme

				selectedTheme.value!.semantic!.ermbrand = ermbrandTheme

				// Load font settings for this theme from JSON
				const loadedFontSettings = loadFontSettingsFromJSON()
				// Also attach to selectedTheme for consistency
				if (loadedFontSettings && loadedFontSettings.length > 0) {
					selectedTheme.value!.fontSettings = loadedFontSettings[0]
				}

				// Update PrimeVue preset with the loaded template
				const preset = convertTemplateUIToPreset(res.templateUI)
				updatePreset(definePreset(Aura, preset))

				// Apply font settings to the application
				applyFontSettingsToPreset(fontSettings.value)

				// Apply chip styles globally
				if (res.selectedTheme?.components) {
					applyGlobalChipStyles(res.selectedTheme.components)
				}

				// Apply progressbar styles globally
				if (res.selectedTheme?.components) {
					applyProgressbarStyles(res.selectedTheme.components)
				}

				// Apply toggleswitch styles globally
				if (res.selectedTheme?.components) {
					applyToggleswitchStyles(res.selectedTheme.components)
				}

				// Apply checkbox styles globally
				if (res.selectedTheme?.components) {
					applyCheckboxStyles(res.selectedTheme.components)
				}

				// Apply sidebar colors from theme
				if (res.selectedTheme?.semantic?.sidebar) {
					applySidebarColors(res.selectedTheme.semantic.sidebar)
				}

				// Apply menu-up (header) colors from theme
				if (res.selectedTheme?.semantic?.menuUp) {
					applyMenuUpColors(res.selectedTheme.semantic.menuUp)
				}

				// Apply landing page utilities button colors from theme
				if (res.selectedTheme?.semantic?.landingpageUtilitiesButtons) {
					applyLandingPageUtilitiesColors(res.selectedTheme.semantic.landingpageUtilitiesButtons)
				}

				// Apply breadcrumb colors from theme
				if ((res.selectedTheme?.semantic as any)?.breadcrumb) {
					if (res.selectedTheme?.components?.breadcrumb) {
					applyBreadcrumbColors(res.selectedTheme.components.breadcrumb)
				}
				}

				// Apply dialog header colors
				applyDialogColors(res.selectedTheme?.semantic)

				// Apply form field colors
				applyFormColors(res.selectedTheme?.semantic)

				// Apply signal colors as CSS variables for VueFlow and other components
				applySignalColors(res.selectedTheme?.semantic)

				// Ensure PrimeIcons work correctly
				ensurePrimeIconsSupport()

				return res
			} else {
				return undefined
			}
		} catch (err) {
			throw new Error(`Error from API: ${err}`)
		}
	}

	async function loadDefaultTemplateFromDB(): Promise<void> {
		try {
			const res = await themeManagerClient.getDefaultTheme()
			if (res) {
				// Store the template data in the state
				id.value = res.id
				templateUI.value = res.templateUI
				selectedTheme.value = res.selectedTheme
				selectedTheme.value!.semantic!.ermbrand = ermbrandTheme

				// Load font settings for this theme from JSON
				const loadedFontSettings = loadFontSettingsFromJSON()
				// Also attach to selectedTheme for consistency
				if (loadedFontSettings && loadedFontSettings.length > 0) {
					selectedTheme.value!.fontSettings = loadedFontSettings[0]
				}

				// Convert TemplateUI to PrimeVue Preset and update the theme
				const preset = convertTemplateUIToPreset(res.templateUI)
				updatePreset(definePreset(Aura, preset))

				// Apply font settings to the application
				applyFontSettingsToPreset(fontSettings.value)

				// Apply chip styles globally
				if (res.selectedTheme?.components) {
					applyGlobalChipStyles(res.selectedTheme.components)
				}

				// Apply toggleswitch styles globally
				if (res.selectedTheme?.components) {
					applyToggleswitchStyles(res.selectedTheme.components)
				}

				// Apply checkbox styles globally
				if (res.selectedTheme?.components) {
					applyCheckboxStyles(res.selectedTheme.components)
				}

				// Apply sidebar colors from theme
				if (res.selectedTheme?.semantic?.sidebar) {
					applySidebarColors(res.selectedTheme.semantic.sidebar)
				}

				// Apply menu-up (header) colors from theme
				if (res.selectedTheme?.semantic?.menuUp) {
					applyMenuUpColors(res.selectedTheme.semantic.menuUp)
				}

				// Apply landing page utilities button colors from theme
				if (res.selectedTheme?.semantic?.landingpageUtilitiesButtons) {
					applyLandingPageUtilitiesColors(res.selectedTheme.semantic.landingpageUtilitiesButtons)
				}

				// Apply breadcrumb colors from theme
				if ((res.selectedTheme?.semantic as any)?.breadcrumb) {
					if (res.selectedTheme?.components?.breadcrumb) {
					applyBreadcrumbColors(res.selectedTheme.components.breadcrumb)
				}
				}

				// Apply downstream distribution styles globally
				if (res.selectedTheme) {
					applyDownstreamDistributionStyles(res.selectedTheme)
				}

				// Apply button styles globally
				if (res.selectedTheme) {
					applyButtonStyles(res.selectedTheme)
				}

				// Ensure PrimeIcons work correctly
				ensurePrimeIconsSupport()
			}
		} catch (err) {
			console.error("Failed to load default template:", err)
			throw new Error(`Error from API: ${err}`)
		}
	}

	async function initDefaultTemplateFromDB(): Promise<void> {
		try {
			// Use axios directly without authentication for initial theme load
			const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/ThemeMgm/getDefaultTheme`)
			const res = response.data as ThemeState

			if (res) {
				// Store the template data in the state
				id.value = res.id
				templateUI.value = res.templateUI
				selectedTheme.value = res.selectedTheme

				selectedTheme.value!.semantic!.ermbrand = ermbrandTheme

				// Load font settings for this theme from JSON
				const loadedFontSettings = loadFontSettingsFromJSON()
				// Also attach to selectedTheme for consistency
				if (loadedFontSettings && loadedFontSettings.length > 0) {
					selectedTheme.value!.fontSettings = loadedFontSettings[0]
				}

				// Convert TemplateUI to PrimeVue Preset and update the theme
				const preset = convertTemplateUIToPreset(res.templateUI)
				definePreset(Aura, preset)

				// Apply font settings to the application
				applyFontSettingsToPreset(fontSettings.value)

				// Apply chip styles globally
				if (res.selectedTheme?.components) {
					applyGlobalChipStyles(res.selectedTheme.components)
				}

				// Apply toggleswitch styles globally
				if (res.selectedTheme?.components) {
					applyToggleswitchStyles(res.selectedTheme.components)
				}

				// Apply checkbox styles globally
				if (res.selectedTheme?.components) {
					applyCheckboxStyles(res.selectedTheme.components)
				}

				// Apply sidebar colors from theme
				if (res.selectedTheme?.semantic?.sidebar) {
					applySidebarColors(res.selectedTheme.semantic.sidebar)
				}

				// Apply menu-up (header) colors from theme
				if (res.selectedTheme?.semantic?.menuUp) {
					applyMenuUpColors(res.selectedTheme.semantic.menuUp)
				}

				// Apply landing page utilities button colors from theme
				if (res.selectedTheme?.semantic?.landingpageUtilitiesButtons) {
					applyLandingPageUtilitiesColors(res.selectedTheme.semantic.landingpageUtilitiesButtons)
				}

				// Apply breadcrumb colors from theme
				if ((res.selectedTheme?.semantic as any)?.breadcrumb) {
					if (res.selectedTheme?.components?.breadcrumb) {
					applyBreadcrumbColors(res.selectedTheme.components.breadcrumb)
				}
				}

				// Apply downstream distribution styles globally
				if (res.selectedTheme) {
					if (res.selectedTheme) {
						applyDownstreamDistributionStyles(res.selectedTheme)
					}
				}

				// Apply button styles globally
				if (res.selectedTheme) {
					if (res.selectedTheme) {
						applyButtonStyles(res.selectedTheme)
					}
				}

				// Ensure PrimeIcons work correctly
				ensurePrimeIconsSupport()
			}
		} catch (err) {
			console.error("Failed to load default template:", err)
			throw new Error(`Error from API: ${err}`)
		}
	}

	// Getters
	const getCurrentTheme = computed(() => themeState.value)
	const getCurrentPreset = computed(() => {
		if (templateUI.value) {
			return definePreset(Aura, convertTemplateUIToPreset(templateUI.value))
		}
		// Return Aura as default preset if no theme is loaded
		return Aura
	})

	// Font Settings Actions - now loads from JSON instead of database
	function loadFontSettingsFromJSON(customTemplateUI?: any): FontSettings[] {
		try {
			const sourceTemplateUI = customTemplateUI || templateUI.value
			if (sourceTemplateUI?.themeSettings) {
				const themeSettings = JSON.parse(sourceTemplateUI.themeSettings)
				const jsonFontSettings = themeSettings.fontSettings

				if (jsonFontSettings) {
					// Convert JSON data to FontSettings format for compatibility
					const settings: FontSettings[] = [
						{
							id: "json-based-id",
							themeId: sourceTemplateUI.id || "",
							fontFamily: jsonFontSettings.fontFamily || "Inter, sans-serif",
							fontSize: jsonFontSettings.fontSize || 14,
							fontWeight: jsonFontSettings.fontWeight || "400",
							lineHeight: jsonFontSettings.lineHeight || 1.5,
							letterSpacing: jsonFontSettings.letterSpacing || 0,
							fontStyle: jsonFontSettings.fontStyle || "normal",
							textDecoration: jsonFontSettings.textDecoration || "none",
							textTransform: jsonFontSettings.textTransform || "none",
							color: jsonFontSettings.color || "#000000",
							isDefault: jsonFontSettings.isDefault !== false,
							// Add toJSON method for API compatibility
							toJSON: function () {
								const { toJSON, init, ...rest } = this
								return rest
							},
							init: function () {
								return this
							},
						},
					]

					fontSettings.value = settings
					return settings
				}
			}

			// Fallback to empty array if no settings found
			fontSettings.value = []
			return fontSettings.value
		} catch (err) {
			console.error("Error loading font settings from JSON:", err)
			fontSettings.value = []
			return fontSettings.value
		}
	}

	// Deprecated database-based loading - kept ONLY for migration from old themes
	// This function will be removed once all themes have been migrated to JSON storage
	async function loadFontSettingsForTheme(themeId: string): Promise<FontSettings[]> {
		console.warn("⚠️ loadFontSettingsForTheme is deprecated but being used for migration purposes")
		// Since fontSettingsClient is no longer available, return empty array
		// Themes should have their font settings in JSON now
		return []

		// OLD CODE - Removed as we no longer use database for font settings
		// try {
		// 	const settings = await fontSettingsClient.getByThemeId(themeId)
		// 	const dbSettings = settings || []
		// 	return dbSettings
		// } catch (err) {
		// 	console.error("Error loading font settings from database:", err)
		// 	return []
		// }
	}

	// DEPRECATED - No longer saving font settings to database
	// Font settings are now stored in templateUI.themeSettings JSON
	// async function saveFontSetting(fontSetting: FontSettings): Promise<FontSettings | undefined> {
	// 	try {
	// 		const result = await fontSettingsClient.save(fontSetting)
	// 		return result
	// 	} catch (err) {
	// 		console.error("Error saving font setting:", err)
	// 		throw new Error(`Error saving font setting: ${err}`)
	// 	}
	// }

	// DEPRECATED - No longer deleting font settings from database
	// Font settings are now stored in templateUI.themeSettings JSON
	// async function deleteFontSetting(fontSettingId: string): Promise<void> {
	// 	try {
	// 		await fontSettingsClient.delete(fontSettingId)
	// 		if (templateUI.value?.id) {
	// 			await loadFontSettingsForTheme(templateUI.value.id)
	// 		}
	// 	} catch (err) {
	// 		console.error("Error deleting font setting:", err)
	// 		throw new Error(`Error deleting font setting: ${err}`)
	// 	}
	// }

	function applyFontSettingsToPreset(fontSettings: FontSettings[]): any {
		if (!fontSettings || fontSettings.length === 0) {
			return {}
		}

		// Use the first (and typically only) font setting as the global default
		const primaryFontSetting = fontSettings[0]

		// Create CSS custom properties for global font settings
		const fontStyles: any = {
			// Global application font settings
			"--app-font-family": primaryFontSetting.fontFamily || "Inter, sans-serif",
			"--app-font-size": `${primaryFontSetting.fontSize || 14}px`,
			"--app-font-weight": primaryFontSetting.fontWeight || "400",
			"--app-line-height": primaryFontSetting.lineHeight || 1.5,
			"--app-letter-spacing": `${primaryFontSetting.letterSpacing || 0}px`,
			"--app-font-style": primaryFontSetting.fontStyle || "normal",
			"--app-text-decoration": primaryFontSetting.textDecoration || "none",
			"--app-text-transform": primaryFontSetting.textTransform || "none",
			"--app-text-color": primaryFontSetting.color || "#000000",
		}

		// Apply styles to document root
		const root = document.documentElement
		Object.entries(fontStyles).forEach(([property, value]) => {
			if (value) {
				root.style.setProperty(property, value.toString())
			}
		})

		// Apply global styles to body and common elements
		applyGlobalFontStyles(primaryFontSetting)

		// Ensure PrimeIcons still work after font changes
		ensurePrimeIconsSupport()

		return fontStyles
	}

	function applySidebarColors(sidebar: any): void {
		// Apply sidebar colors from theme globally
		if (sidebar?.backgroundColor) {
			document.documentElement.style.setProperty("--main-sidebar-background-color", sidebar.backgroundColor)
		}
		if (sidebar?.textColor) {
			document.documentElement.style.setProperty("--main-sidebar-text-color", sidebar.textColor)
		}
	}

	function applyMenuUpColors(menuUp: any): void {
		// Apply menu-up (header) colors from theme globally
		if (menuUp?.backgroundColor) {
			document.documentElement.style.setProperty("--p-menu-up-background-color", menuUp.backgroundColor)
		}
		if (menuUp?.textColor) {
			document.documentElement.style.setProperty("--p-menu-up-text-color", menuUp.textColor)
		}
		if (menuUp?.borderBottom) {
			document.documentElement.style.setProperty("--p-menu-up-border-bottom", menuUp.borderBottom)
		}
		if (menuUp?.buttonColor) {
			document.documentElement.style.setProperty("--p-menu-up-button-color", menuUp.buttonColor)
		}
		if (menuUp?.buttonTextColor) {
			document.documentElement.style.setProperty("--p-menu-up-button-text-color", menuUp.buttonTextColor)
		}
		if (menuUp?.buttonColorHover) {
			document.documentElement.style.setProperty("--p-menu-up-button-color-hover", menuUp.buttonColorHover)
		}
	}

	function applyLandingPageUtilitiesColors(landingpageUtilitiesButtons: any): void {
		// Apply landing page utilities button colors from theme globally
		if (landingpageUtilitiesButtons?.backgroundColor) {
			document.documentElement.style.setProperty("--p-landingpage-utilities-buttons-background-color", landingpageUtilitiesButtons.backgroundColor)
		}
		if (landingpageUtilitiesButtons?.backgroundColorHover) {
			document.documentElement.style.setProperty(
				"--p-landingpage-utilities-buttons-background-color-hover",
				landingpageUtilitiesButtons.backgroundColorHover
			)
		}
		if (landingpageUtilitiesButtons?.textColor) {
			document.documentElement.style.setProperty("--p-landingpage-utilities-buttons-text-color", landingpageUtilitiesButtons.textColor)
		}
		if (landingpageUtilitiesButtons?.textColorHover) {
			document.documentElement.style.setProperty("--p-landingpage-utilities-buttons-text-color-hover", landingpageUtilitiesButtons.textColorHover)
		}
		if (landingpageUtilitiesButtons?.textProjectFigures) {
			document.documentElement.style.setProperty(
				"--p-landingpage-utilities-buttons-text-project-figures",
				landingpageUtilitiesButtons.textProjectFigures
			)
		}
	}

	function applyBreadcrumbColors(breadcrumb: any): void {
		// Apply breadcrumb colors from theme globally
		// Updated to use components.breadcrumb.colorScheme.light structure
		if (breadcrumb?.colorScheme?.light?.root?.background) {
			document.documentElement.style.setProperty("--main-breadcrumb-background-color", breadcrumb.colorScheme.light.root.background)
		}
		if (breadcrumb?.colorScheme?.light?.item?.color) {
			document.documentElement.style.setProperty("--main-breadcrumb-text-color", breadcrumb.colorScheme.light.item.color)
		}
		// Apply icon color (usually same as text color)
		if (breadcrumb?.colorScheme?.light?.item?.icon?.color) {
			document.documentElement.style.setProperty("--main-breadcrumb-icon-color", breadcrumb.colorScheme.light.item.icon.color)
		}
	}

	function applyToggleswitchStyles(components: any): void {
		// Apply toggleswitch colors globally if they exist
		if (!components?.toggleswitch?.colorScheme?.light?.root) return

		const root = components.toggleswitch.colorScheme.light.root
		const checkedBg = root.checkedBackground || "#6366F1"
		const checkedHoverBg = root.checkedHoverBackground || lightenColor(checkedBg, 15)
		const uncheckedBg = root.background || "#d1d5db"
		const uncheckedHoverBg = root.hoverBackground || "#9ca3af"

		// Create or update toggleswitch styles
		let toggleswitchStyleElement = document.getElementById("global-toggleswitch-styles") as HTMLStyleElement

		if (!toggleswitchStyleElement) {
			toggleswitchStyleElement = document.createElement("style")
			toggleswitchStyleElement.id = "global-toggleswitch-styles"
			document.head.appendChild(toggleswitchStyleElement)
		}

		const toggleswitchCss = `
			/* Global toggleswitch color styles */
			.p-toggleswitch:not(.p-toggleswitch-checked) .p-toggleswitch-slider {
				background-color: ${uncheckedBg} !important;
			}

			.p-toggleswitch:not(.p-toggleswitch-checked):hover .p-toggleswitch-slider {
				background-color: ${uncheckedHoverBg} !important;
			}

			.p-toggleswitch-checked .p-toggleswitch-slider {
				background-color: ${checkedBg} !important;
			}

			.p-toggleswitch-checked:hover .p-toggleswitch-slider {
				background-color: ${checkedHoverBg} !important;
			}
		`

		toggleswitchStyleElement.textContent = toggleswitchCss
	}

	function applyCheckboxStyles(components: any): void {
		// Apply checkbox colors globally if they exist
		if (!components?.checkbox?.colorScheme?.light?.root) return

		const root = components.checkbox.colorScheme.light.root
		const checkedBg = root.checkedBackground || "#6366F1"
		const checkedColor = root.checkedColor || "#ffffff"
		const hoverBg = root.hoverBackground || lightenColor(checkedBg, 15)

		// Create or update checkbox styles
		let checkboxStyleElement = document.getElementById("global-checkbox-styles") as HTMLStyleElement

		if (!checkboxStyleElement) {
			checkboxStyleElement = document.createElement("style")
			checkboxStyleElement.id = "global-checkbox-styles"
			document.head.appendChild(checkboxStyleElement)
		}

		const checkboxCss = `
			/* Global checkbox color styles - PrimeVue 3 */
			.p-checkbox.p-highlight .p-checkbox-box {
				background-color: ${checkedBg} !important;
				border-color: ${checkedBg} !important;
			}

			.p-checkbox.p-highlight .p-checkbox-box .p-checkbox-icon {
				color: ${checkedColor} !important;
			}

			.p-checkbox.p-highlight:hover .p-checkbox-box {
				background-color: ${hoverBg} !important;
				border-color: ${hoverBg} !important;
			}

			/* PrimeVue 4 checkbox styling - class based */
			.p-checkbox-checked .p-checkbox-box {
				background-color: ${checkedBg} !important;
				border-color: ${checkedBg} !important;
			}

			.p-checkbox-checked .p-checkbox-box .p-checkbox-icon {
				color: ${checkedColor} !important;
			}

			.p-checkbox-checked:hover .p-checkbox-box {
				background-color: ${hoverBg} !important;
				border-color: ${hoverBg} !important;
			}

			/* PrimeVue 4 checkbox styling - data attribute based */
			.p-checkbox[data-p-checked="true"] .p-checkbox-box {
				background-color: ${checkedBg} !important;
				border-color: ${checkedBg} !important;
			}

			.p-checkbox[data-p-checked="true"] .p-checkbox-box .p-checkbox-icon {
				color: ${checkedColor} !important;
			}

			.p-checkbox[data-p-checked="true"]:hover .p-checkbox-box {
				background-color: ${hoverBg} !important;
				border-color: ${hoverBg} !important;
			}

			/* PrimeVue 4 checkbox styling - highlight attribute based */
			.p-checkbox[data-p-highlight="true"] .p-checkbox-box {
				background-color: ${checkedBg} !important;
				border-color: ${checkedBg} !important;
			}

			.p-checkbox[data-p-highlight="true"] .p-checkbox-box .p-checkbox-icon {
				color: ${checkedColor} !important;
			}

			.p-checkbox[data-p-highlight="true"]:hover .p-checkbox-box {
				background-color: ${hoverBg} !important;
				border-color: ${hoverBg} !important;
			}
		`

		checkboxStyleElement.textContent = checkboxCss
	}


	function applyGlobalChipStyles(components: any): void {
		// Apply chip colors globally if they exist
		if (!components?.chip?.colorScheme?.light?.root) return

		const chipColors = components.chip.colorScheme.light.root

		// Create or update chip styles
		let chipStyleElement = document.getElementById("global-chip-styles") as HTMLStyleElement

		if (!chipStyleElement) {
			chipStyleElement = document.createElement("style")
			chipStyleElement.id = "global-chip-styles"
			document.head.appendChild(chipStyleElement)
		}

		const chipCss = `
			/* Global chip color styles */
			.p-chip {
				background-color: ${chipColors.background} !important;
				color: ${chipColors.color || "#374151"} !important;
			}
			
			.p-chip .p-chip-text {
				color: ${chipColors.color || "#374151"} !important;
			}
			
			.p-chip .p-chip-remove-icon,
			.p-chip .pi {
				color: ${chipColors.iconColor || chipColors.color || "#374151"} !important;
			}
			
			.p-chip:hover {
				background-color: ${chipColors.hoverBackground || chipColors.background} !important;
				color: ${chipColors.hoverColor || chipColors.color || "#374151"} !important;
			}
		`

		chipStyleElement.textContent = chipCss
	}

	function applyGlobalFontStyles(fontSetting: FontSettings): void {
		// Create a comprehensive CSS style element for global font application
		let styleElement = document.getElementById("global-font-styles") as HTMLStyleElement

		if (!styleElement) {
			styleElement = document.createElement("style")
			styleElement.id = "global-font-styles"
			document.head.appendChild(styleElement)
		}

		const css = `
			/* Global font settings applied to body and common elements */
			body {
				font-family: ${fontSetting.fontFamily || "Inter, sans-serif"} !important;
				font-size: ${fontSetting.fontSize || 14}px !important;
				font-weight: ${fontSetting.fontWeight || "400"} !important;
				line-height: ${fontSetting.lineHeight || 1.5} !important;
				letter-spacing: ${fontSetting.letterSpacing || 0}px !important;
				font-style: ${fontSetting.fontStyle || "normal"} !important;
				color: ${fontSetting.color || "#000000"} !important;
			}

			/* Apply to all text elements (excluding text preview and icons) */
			p, span:not([class*="pi-"]), div:not(.text-preview), label, h1, h2, h3, h4, h5, h6,
			.p-component:not([class*="pi-"]),
			.p-button-label,
			.p-inputtext,
			.p-textarea,
			.p-dropdown-label,
			.p-multiselect-label,
			.p-checkbox-label,
			.p-radiobutton-label,
			.p-selectbutton .p-button-label,
			.p-togglebutton .p-button-label,
			.p-tabmenu-nav a,
			.p-breadcrumb a,
			.p-steps-title,
			.p-menuitem-text,
			.p-datatable th,
			.p-datatable td,
			.p-toast-message-text,
			.p-toast-summary,
			.p-toast-detail,
			.p-dialog-title,
			.p-card-title,
			.p-card-subtitle,
			.p-card-content,
			.p-panel-title {
				font-family: ${fontSetting.fontFamily || "Inter, sans-serif"} !important;
				font-weight: ${fontSetting.fontWeight || "400"} !important;
				line-height: ${fontSetting.lineHeight || 1.5} !important;
				letter-spacing: ${fontSetting.letterSpacing || 0}px !important;
				font-style: ${fontSetting.fontStyle || "normal"} !important;
			}

			/* Ensure PrimeIcons are not affected by global font settings */
			.pi, [class*="pi-"] {
				font-family: "primeicons" !important;
			}

			/* Ensure headings maintain hierarchy while using global font */
			h1 { font-size: ${(fontSetting.fontSize || 14) * 2.2}px !important; font-weight: 700 !important; }
			h2 { font-size: ${(fontSetting.fontSize || 14) * 1.8}px !important; font-weight: 600 !important; }
			h3 { font-size: ${(fontSetting.fontSize || 14) * 1.5}px !important; font-weight: 600 !important; }
			h4 { font-size: ${(fontSetting.fontSize || 14) * 1.3}px !important; font-weight: 500 !important; }
			h5 { font-size: ${(fontSetting.fontSize || 14) * 1.1}px !important; font-weight: 500 !important; }
			h6 { font-size: ${fontSetting.fontSize || 14}px !important; font-weight: 500 !important; }

			/* Form elements specific styling */
			.p-inputtext, .p-textarea, .p-dropdown, .p-multiselect {
				font-size: ${fontSetting.fontSize || 14}px !important;
			}

			/* Table specific styling */
			.p-datatable {
				font-size: ${fontSetting.fontSize || 14}px !important;
			}

			/* Button specific styling */
			.p-button {
				font-size: ${fontSetting.fontSize || 14}px !important;
			}

			/* Menu and navigation */
			.p-menu .p-menuitem-text,
			.p-tabmenu .p-tabmenu-nav .p-tabmenu-nav-link {
				font-size: ${fontSetting.fontSize || 14}px !important;
			}

			/* Override for text preview - allow inline styles to take precedence */
			.text-preview {
				font-family: inherit;
				font-size: inherit;
				font-weight: inherit;
				line-height: inherit;
				letter-spacing: inherit;
				color: inherit;
				font-style: inherit;
			}
		`

		styleElement.textContent = css
	}

	function applyDownstreamDistributionStyles(themeTemplate: ThemeTemplate): void {
		// Apply downstream distribution stepRow colors as CSS variables
		if (themeTemplate?.semantic?.downstreamDistributionTable?.stepRow) {
			const stepRow = themeTemplate.semantic.downstreamDistributionTable.stepRow
			const root = document.documentElement

			if (stepRow.backgroundColor) {
				root.style.setProperty("--p-downstream-distribution-table-step-name-row-background-color", stepRow.backgroundColor)
			}

			if (stepRow.textColor) {
				root.style.setProperty("--p-downstream-distribution-table-step-name-row-color", stepRow.textColor)
			}
		}
	}

	function applyButtonStyles(themeTemplate: ThemeTemplate): void {
		// Apply button colors for help and contrast severities using CSS custom properties
		const components = themeTemplate?.components as any
		if (!components?.button?.colorScheme?.light) {
			return
		}

		const buttonStyles = components.button.colorScheme.light
		const root = document.documentElement

		// Apply help button styles as CSS variables
		if (buttonStyles.root?.help) {
			const helpStyle = buttonStyles.root.help

			if (helpStyle.background) {
				root.style.setProperty("--p-button-help-background", helpStyle.background)
			}
			if (helpStyle.color) {
				root.style.setProperty("--p-button-help-color", helpStyle.color)
			}
			if (helpStyle.borderColor) {
				root.style.setProperty("--p-button-help-border-color", helpStyle.borderColor)
			}
		}

		if (buttonStyles.outlined?.help) {
			const helpStyle = buttonStyles.outlined.help

			if (helpStyle.color) {
				root.style.setProperty("--p-button-help-outlined-color", helpStyle.color)
			}
			if (helpStyle.borderColor) {
				root.style.setProperty("--p-button-help-outlined-border-color", helpStyle.borderColor)
			}
		}

		if (buttonStyles.text?.help) {
			const helpStyle = buttonStyles.text.help

			if (helpStyle.color) {
				root.style.setProperty("--p-button-help-text-color", helpStyle.color)
			}
		}

		// Apply contrast button styles as CSS variables
		if (buttonStyles.root?.contrast) {
			const contrastStyle = buttonStyles.root.contrast

			if (contrastStyle.background) {
				root.style.setProperty("--p-button-contrast-background", contrastStyle.background)
			}
			if (contrastStyle.color) {
				root.style.setProperty("--p-button-contrast-color", contrastStyle.color)
			}
			if (contrastStyle.borderColor) {
				root.style.setProperty("--p-button-contrast-border-color", contrastStyle.borderColor)
			}
		}

		if (buttonStyles.outlined?.contrast) {
			const contrastStyle = buttonStyles.outlined.contrast

			if (contrastStyle.color) {
				root.style.setProperty("--p-button-contrast-outlined-color", contrastStyle.color)
			}
			if (contrastStyle.borderColor) {
				root.style.setProperty("--p-button-contrast-outlined-border-color", contrastStyle.borderColor)
			}
		}

		if (buttonStyles.text?.contrast) {
			const contrastStyle = buttonStyles.text.contrast

			if (contrastStyle.color) {
				root.style.setProperty("--p-button-contrast-text-color", contrastStyle.color)
			}
		}

		// Apply the CSS rules to use these custom properties
		ensureButtonStylesCSS()
	}

	function ensureButtonStylesCSS(): void {
		// Create or update button severity styles using CSS custom properties
		let buttonStyleElement = document.getElementById("button-severity-styles") as HTMLStyleElement

		if (!buttonStyleElement) {
			buttonStyleElement = document.createElement("style")
			buttonStyleElement.id = "button-severity-styles"
			document.head.appendChild(buttonStyleElement)
		}

		const css = `
			/* Help Button Styles - Lower priority to allow inline styles to override */
			.p-button-help:not(.p-button-outlined):not(.p-button-text) {
				background-color: var(--p-button-help-background, #8b5cf6);
				color: var(--p-button-help-color, white);
				border-color: var(--p-button-help-border-color, #8b5cf6);
			}
			
			.p-button-help.p-button-outlined {
				color: var(--p-button-help-outlined-color, #8b5cf6);
				border-color: var(--p-button-help-outlined-border-color, #8b5cf6);
				background-color: transparent;
			}
			
			.p-button-help.p-button-text {
				color: var(--p-button-help-text-color, #8b5cf6);
				background-color: transparent;
				border-color: transparent;
			}
			
			/* Contrast Button Styles - Lower priority to allow inline styles to override */
			.p-button-contrast:not(.p-button-outlined):not(.p-button-text) {
				background-color: var(--p-button-contrast-background, #374151);
				color: var(--p-button-contrast-color, white);
				border-color: var(--p-button-contrast-border-color, #374151);
			}
			
			.p-button-contrast.p-button-outlined {
				color: var(--p-button-contrast-outlined-color, #374151);
				border-color: var(--p-button-contrast-outlined-border-color, #374151);
				background-color: transparent;
			}
			
			.p-button-contrast.p-button-text {
				color: var(--p-button-contrast-text-color, #374151);
				background-color: transparent;
				border-color: transparent;
			}
		`

		buttonStyleElement.textContent = css
	}

	function applyProgressbarStyles(components: any): void {
		const value = components?.progressbar?.colorScheme?.light?.value
		if (!value?.background) return

		const root = document.documentElement
		root.style.setProperty("--p-progressbar-value-background", value.background)
		if (value.color) root.style.setProperty("--p-progressbar-label-color", value.color)

		let styleEl = document.getElementById("global-progressbar-styles") as HTMLStyleElement
		if (!styleEl) {
			styleEl = document.createElement("style")
			styleEl.id = "global-progressbar-styles"
			document.head.appendChild(styleEl)
		}
		styleEl.textContent = `
			.p-progressbar-value { background: var(--p-progressbar-value-background) !important; }
			.p-progressbar-label, .progress-text { color: var(--p-progressbar-label-color, #ffffff) !important; }
		`
	}

	function applyDialogColors(semantic: any): void {
		if (!semantic?.dialog) return
		const root = document.documentElement
		if (semantic.dialog.titleBackgroundColor) root.style.setProperty("--p-dialog-title-background", semantic.dialog.titleBackgroundColor)
		if (semantic.dialog.titleTextColor) root.style.setProperty("--p-dialog-title-color", semantic.dialog.titleTextColor)
	}

	function applyFormColors(semantic: any): void {
		if (!semantic?.formColors) return
		const root = document.documentElement
		const f = semantic.formColors
		if (f.errorColor) root.style.setProperty("--p-form-error-color", f.errorColor)
		if (f.labelColor) root.style.setProperty("--p-form-label-color", f.labelColor)
		if (f.borderColor) root.style.setProperty("--p-form-border-color", f.borderColor)
		if (f.focusBorderColor) root.style.setProperty("--p-form-focus-border-color", f.focusBorderColor)
		if (f.warningIconColor) root.style.setProperty("--p-form-warning-icon-color", f.warningIconColor)
	}

	function applySignalColors(semantic: any): void {
		if (!semantic) return
		const root = document.documentElement
		if (semantic.redSignal) root.style.setProperty("--p-red-signal-color", semantic.redSignal)
		if (semantic.yellowSignal) {
			root.style.setProperty("--p-yellow-signal-color", semantic.yellowSignal)
			root.style.setProperty("--p-yellow-signal-light-color", lightenColor(semantic.yellowSignal, 80))
		}
		if (semantic.greenSignal) {
			root.style.setProperty("--p-green-signal-color", semantic.greenSignal)
			root.style.setProperty("--p-green-signal-light-color", lightenColor(semantic.greenSignal, 80))
		}
	}

	function ensurePrimeIconsSupport(): void {
		// Create or update icon support styles
		let iconStyleElement = document.getElementById("primeicons-support") as HTMLStyleElement

		if (!iconStyleElement) {
			iconStyleElement = document.createElement("style")
			iconStyleElement.id = "primeicons-support"
			document.head.appendChild(iconStyleElement)
		}

		const iconCss = `
			/* Ensure PrimeIcons work correctly */
			.pi, [class*="pi-"], [class^="pi-"] {
				font-family: "primeicons" !important;
				font-style: normal !important;
				font-weight: normal !important;
				font-variant: normal !important;
				text-transform: none !important;
				line-height: 1 !important;
				letter-spacing: normal !important;
				speak: none;
				-webkit-font-smoothing: antialiased;
				-moz-osx-font-smoothing: grayscale;
			}
		`

		iconStyleElement.textContent = iconCss
	}

	async function loadUserThemeOrDefault(retryCount: number = 0): Promise<void> {
		try {
			// Temporary implementation using fetch API until TypeScript is updated
			const token = localStorage.getItem("token")
			if (!token) {
				if (retryCount < 3) {
					setTimeout(() => loadUserThemeOrDefault(retryCount + 1), 1000)
					return
				} else {
					await loadDefaultTemplateFromDB()
					return
				}
			}

			const response = await fetch(`${import.meta.env.VITE_API_URL}/api/userThemeSelection/getCurrentUserTheme`, {
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
			})

			if (response.ok) {
				const userTheme = await response.json()

				if (userTheme && userTheme.templateUI && userTheme.id) {
					await loadSingleCompleteTemplate(userTheme.id, "live")
				} else {
					await loadDefaultTemplateFromDB()
				}
			} else {
				const errorText = await response.text()

				// If unauthorized and we have retries left, try again
				if (response.status === 401 && retryCount < 3) {
					setTimeout(() => loadUserThemeOrDefault(retryCount + 1), 2000)
					return
				}

				await loadDefaultTemplateFromDB()
			}
		} catch (error) {
			await loadDefaultTemplateFromDB()
		}
	}

	return {
		// State
		themeState,
		id,
		templateUI,
		selectedTheme,
		fontSettings,
		// Actions
		loadSingleTemplate,
		saveSingleTemplate,
		loadSingleCompleteTemplate,
		loadDefaultTemplateFromDB,
		initDefaultTemplateFromDB,
		loadUserThemeOrDefault,
		// Font Settings Actions - JSON based only
		loadFontSettingsFromJSON,
		loadFontSettingsForTheme, // Deprecated - returns empty array, kept only for migration compatibility
		// saveFontSetting, // REMOVED - no longer saving to database
		// deleteFontSetting, // REMOVED - no longer deleting from database
		applyFontSettingsToPreset,
		applyGlobalFontStyles,
		applyGlobalChipStyles,
		applyToggleswitchStyles,
		applyCheckboxStyles,
		applyMenuUpColors,
		applyBreadcrumbColors,
		applyDownstreamDistributionStyles,
		ensurePrimeIconsSupport,
		// Getters
		getCurrentTheme,
		getCurrentPreset,
	}
})
